export interface StoryboardFrame {
  id: string
  page: string
  title: string
  cue: string
  cta: string
  purpose: string
  logic?: string
  /** Marks the two alternative split routes. */
  variant?: boolean
}

export const storyboard = {
  label: 'Storyboard (PDF)',
  eyebrow: 'Internal · storyboard',
  title: 'NOK Eye Care Pulse — page by page',
  intro:
    'Every screen of the self-guided Snak in order, with the cue type, the CTA and what each page is doing. Pages 4b and 4c are the alternative routes created by the split after Question 1.',
  download: 'Download PDF',
  close: 'Close',
  footer: 'NOK 2026 · Supported by AbbVie · internal concept, not for external use',
  frames: [
    {
      id: 'opening',
      page: 'Page 1',
      title: 'Promise',
      cue: 'Cover',
      cta: 'Add your perspective',
      purpose:
        'Frames the pressure in the delegate’s own terms — more patients, the same specialist hours — and promises peer comparison after every answer. No product, no disease area, no branded solution.',
      logic: 'Footer states responses are aggregated and anonymous.',
    },
    {
      id: 'q1',
      page: 'Page 2',
      title: 'Where is the biggest opportunity?',
      cue: 'Ranking cue · 1–3',
      cta: 'See what your peers think',
      purpose:
        'The strategic question. Ranking all three areas forces a priority rather than allowing agreement with everything.',
      logic:
        'A number taken in one card is locked in the others; free numbers show green. All three ranks required before the CTA unlocks.',
    },
    {
      id: 'q1r',
      page: 'Page 3',
      title: 'Your #1 vs the room',
      cue: 'Peer comparison',
      cta: 'Go deeper',
      purpose:
        'The first reward. Own answer first, then the live NOK ranking, plus a neutral read on whether most peers see it the same way.',
      logic: 'Percentages are live and include this response. Nothing is framed as correct.',
    },
    {
      id: 'q2',
      page: 'Page 4',
      title: 'Inside the chosen area',
      cue: 'Single select + free text',
      cta: 'Compare with peers',
      purpose:
        'The tactical question, shown only for the area the delegate ranked #1. Here: the Treatment route.',
      logic:
        'Split on the #1 ranking. “Something else” opens a short free-text field and requires text before continuing.',
    },
    {
      id: 'q2-diagnosis',
      page: 'Page 4b',
      title: 'Alternative route — Diagnosis',
      cue: 'Single select + free text',
      cta: 'Compare with peers',
      purpose: 'Shown instead of page 4 when Diagnosis is ranked #1.',
      variant: true,
    },
    {
      id: 'q2-followup',
      page: 'Page 4c',
      title: 'Alternative route — Follow-up',
      cue: 'Single select + free text',
      cta: 'Compare with peers',
      purpose: 'Shown instead of page 4 when Follow-up is ranked #1.',
      variant: true,
    },
    {
      id: 'q2r',
      page: 'Page 5',
      title: 'Peers within that area',
      cue: 'Peer comparison',
      cta: 'Continue',
      purpose:
        'Shows how many delegates who prioritised the same area chose the same sub-theme, then the full distribution. All three routes merge after this page.',
      logic:
        'A free-text answer is acknowledged instead of being categorised: “Thank you — your response has been added to the collective picture.”',
    },
    {
      id: 'q3',
      page: 'Page 6',
      title: 'Wider care team',
      cue: 'Rating cue · 1–5',
      cta: 'See the NOK view',
      purpose:
        'Operational question one — workforce. Examples are given so the scale is read consistently.',
    },
    {
      id: 'q3r',
      page: 'Page 7',
      title: 'Your rating vs the NOK average',
      cue: 'Peer comparison',
      cta: 'Next question',
      purpose:
        'Own rating, running average, share rating 4 or 5, and whether the delegate sits with or apart from the room.',
    },
    {
      id: 'q4',
      page: 'Page 8',
      title: 'Technology',
      cue: 'Rating cue · 1–5',
      cta: 'Compare with peers',
      purpose:
        'Operational question two. Deliberately avoids naming AI and never positions technology as the answer.',
    },
    {
      id: 'q4r',
      page: 'Page 9',
      title: 'Your rating vs the NOK average',
      cue: 'Peer comparison',
      cta: 'One last question',
      purpose: 'Same reward pattern, keeping the rhythm predictable before the open question.',
    },
    {
      id: 'q5',
      page: 'Page 10',
      title: 'If you could change one thing',
      cue: 'Open response',
      cta: 'Add my voice',
      purpose:
        'The listening question. Field is completely open and comes first; optional prompt chips sit underneath.',
      logic:
        'Chips are generated from themes already recurring in peer responses and reorder as the congress progresses. Tapping one adds wording the delegate can still edit. “Clear my answer” resets the field.',
    },
    {
      id: 'q5r',
      page: 'Page 11',
      title: 'Your voice has been added',
      cue: 'Theme cluster',
      cta: 'See the NOK picture',
      purpose:
        'Reflects the answer back, then shows the closest emerging themes so the room feels alive and learning.',
      logic: 'Themes come from submitted responses only — nothing is invented or implied as representative.',
    },
    {
      id: 'snapshot',
      page: 'Page 12',
      title: 'The collective snapshot',
      cue: 'Summary',
      cta: 'See all live results',
      purpose:
        'The give-back: leading opportunity, top priority within it, both operational ratings and the leading open theme, with findings promised back after NOK.',
      logic: 'Only brand presence is the “Supported by AbbVie” line. No product, DME or Midena content.',
    },
  ] as StoryboardFrame[],
}
